/* app_judgeme.js — deux comportements du widget d'avis Judge.me sur les fiches
   produit. Copie de référence du dépôt parent : l'original déployé est
   `assets/app_judgeme.js` du thème live (theme-prod/ a son propre .git, et en cas
   de doute le thème live fait foi). Chargé en `defer` depuis `layout/theme.liquid`
   sous `template.name == 'product'`, aux côtés de `assets/app_judgeme.css`.

   ── 1. Les étoiles du haut de fiche ouvrent l'onglet des avis ──
   L'app pose bien un handler sur son badge, mais son `_openReviewTab` ne sait
   ouvrir que ce qu'elle connaît : un `jdgmSettings.revTabSelector` (non réglé
   ici), l'app EasyTabs, ou des onglets rendus en `<a>` / `li.tab__item`. Les
   onglets du thème sont des `<button role="tab">` de `sections/tabs-content.liquid`
   — rien ne correspond, le clic ne révélait donc jamais le widget.
   Hors onglet (section d'avis, 24/09), le clic mène à la section plutôt qu'au
   widget : voir `scrollToReviewsSection`.

   ── 2. Le résumé « Les clients disent » remonte en tête du widget ──
   Le widget est réglé en header `sidebar` (`review_widget_header_theme`), et
   l'app place le résumé IA dans le Stack de la colonne de gauche, bornée à
   300 px par sa propre grille :
     .jm-review-widget--sidebar-header .jm-review-widget__header-and-body {
       display: grid; grid-template-columns: minmax(auto, 300px) 1fr; … }
   Le résumé n'étant pas un enfant direct de cette grille, aucun CSS ne peut
   l'en sortir : il faut déplacer le nœud. Le reste (pleine largeur, espacement)
   est dans `assets/app_judgeme.css`, sur `[data-wb-ai-summary-hoisted]`.

   ── Avis servis en HTML (30/09/2026) ──
   Sur une fiche qui a ses données de widget, la section rend les avis en HTML
   (`[data-wb-reviews]`) et plus le widget Vue : « Voir plus », tri, filtre, photos
   et « Écrire un avis » sont dans `assets/wb-product-reviews.js`. Ici, seul le
   badge du haut de fiche vise ce bloc (`reviewsRoot()`) ; les sections 1 et 2
   ne servent plus qu'au repli des fiches sans données, où le widget est rendu.

   ⚠️ Aucun sélecteur `[data-v-<hash>]` ici ni dans le CSS : ces hashes changent à
   chaque release de l'app et tout ce qui les cible casse en silence. */
(function () {
  'use strict';

  var WIDGET_ID = 'judgeme_product_reviews';
  var HOISTED_ATTR = 'data-wb-ai-summary-hoisted';
  var BLOCK_SELECTOR = '[data-wb-reviews]';

  function widgetEl() {
    return document.getElementById(WIDGET_ID);
  }

  /* Ce qui porte les avis sur la fiche : le widget s'il est monté, sinon le bloc
     HTML de la section. Les deux portent l'id produit en `data-product-id`. */
  function reviewsRoot() {
    return widgetEl() || document.querySelector(BLOCK_SELECTOR);
  }

  /* ─────────────── 1. Badge du haut → onglet des avis ─────────────── */

  /* Jamais par le libellé de l'onglet, qui est traduit et paramétrable : on part
     du widget, on remonte au panneau qui le porte, et l'id de l'onglet s'en
     déduit — `tabs-content.liquid` nomme le panneau `tab-<id>` pour `<id>`. */
  function reviewsTab(widget) {
    var panel = widget.closest('[role="tabpanel"]');
    if (!panel || !panel.id || panel.id.slice(0, 4) !== 'tab-') return null;
    var tab = document.getElementById(panel.id.slice(4));
    return tab && tab.getAttribute('role') === 'tab' ? tab : null;
  }

  function openReviewsTab(widget) {
    var tab = reviewsTab(widget);
    if (!tab) return false;

    tab.click();

    var anchor = tab.closest('tabs-component') || tab;
    if (typeof anchor.scrollIntoView === 'function') {
      anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    if (typeof tab.focus === 'function') tab.focus({ preventScroll: true });
    return true;
  }

  /* Le badge du produit courant, et lui seul : les cartes produit des blocs de
     recommandation portent le même markup avec la classe `pcard-reviews`. */
  function ownBadge(target, root) {
    if (!target || typeof target.closest !== 'function') return null;
    var badge = target.closest('.jdgm-preview-badge');
    if (!badge || badge.classList.contains('pcard-reviews')) return null;
    var id = root.getAttribute('data-product-id') || root.getAttribute('data-id') || '';
    return badge.getAttribute('data-id') === id ? badge : null;
  }

  /* Depuis le 24/09 le widget vit hors onglet, dans `sections/wb-product-reviews.liquid`.
     Le défilement de l'app (`jdgm.scrollTo`) vise `#judgeme_product_reviews` à
     `scrollTopOffset` = 30 px du haut : sous le header collant, qui cache le titre de
     la section et le haut du widget. On vise la section, repérée par l'attribut que
     lit aussi la barre d'ancres ; son `scroll-margin-top` réserve la hauteur du header. */
  function scrollToReviewsSection(widget) {
    var section = widget.closest('[data-wb-anchor-label]');
    if (!section || typeof section.scrollIntoView !== 'function') return false;
    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return true;
  }

  function activate(event) {
    var root = reviewsRoot();
    if (!root || !ownBadge(event.target, root)) return;
    // Ni onglet ni section d'avis : on laisse l'app faire.
    if (!openReviewsTab(root) && !scrollToReviewsSection(root)) return;

    /* Écouté en capture, donc avant le handler que l'app pose sur le badge :
       sans cet arrêt, son `_scrollDownToWidget` lance un second défilement
       concurrent du nôtre. */
    event.preventDefault();
    event.stopPropagation();
  }

  function activateOnKey(event) {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
    /* Les étoiles sont un `<span role="button" tabindex="0">` : l'app ne leur
       donne aucun gestionnaire clavier, il n'y a donc rien à recouvrir ici. */
    activate(event);
  }

  /* ─────────────── 2. Résumé IA en tête du widget ─────────────── */

  function hoistSummary(widget) {
    var root = widget.querySelector('.jm-review-widget');
    var summary = widget.querySelector('.jm-ai-insights');
    if (!root || !summary) return;
    if (summary.parentNode === root && root.firstChild === summary) return;

    try {
      root.insertBefore(summary, root.firstChild);
      summary.setAttribute(HOISTED_ATTR, '');
    } catch (e) {
      /* Le nœud appartient à Vue : si le déplacement est refusé, le résumé
         reste dans la colonne de gauche — dégradé, pas cassé. */
    }
  }

  function watchSummary(widget) {
    hoistSummary(widget);
    if (typeof MutationObserver !== 'function') return;

    var pending = false;
    var observer = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      /* L'app monte ses nœuds par salves : une seule passe par salve, sinon
         chaque insertion en relance une. */
      setTimeout(function () {
        pending = false;
        hoistSummary(widget);
      }, 0);
    });
    observer.observe(widget, { childList: true, subtree: true });
  }

  /* ─────────────── Amorçage ─────────────── */

  function init() {
    var widget = widgetEl();
    var block = document.querySelector(BLOCK_SELECTOR);
    if (!widget && !block) return;

    document.addEventListener('click', activate, true);
    document.addEventListener('keydown', activateOnKey, true);
    // Widget servi d'emblée : fiche sans données de widget, repli de la section.
    if (widget) watchSummary(widget);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
